import { authClient, apiClient } from '@/api/client';

// User type based on API response
export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  org_name: string;
  active: boolean;
  isadmin: boolean;
}

// Storage key for persisted user data
const USER_STORAGE_KEY = 'rainmaker_admin_user';

/**
 * Get the current user from sessionStorage.
 * Returns null if not logged in.
 */
export function getCurrentUser(): AuthUser | null {
  try {
    const stored = sessionStorage.getItem(USER_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as AuthUser;
  } catch {
    return null;
  }
}

/**
 * Save user to sessionStorage after successful auth.
 */
export function saveUser(user: AuthUser): void {
  sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

/**
 * Clear user from sessionStorage on logout.
 */
export function clearStoredUser(): void {
  sessionStorage.removeItem(USER_STORAGE_KEY);
}

/**
 * Sign in with email and password.
 * POST /adm/signin { email, password }
 * Returns user object (session cookie set by backend).
 * Also returns status for per-status error mapping in Login.tsx.
 */
export async function signIn(
  email: string,
  password: string
): Promise<{ user: AuthUser | null; error?: string; status?: number }> {
  const { data, error } = await authClient<AuthUser | { user: AuthUser }>(
    '/signin',
    {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }
  );

  if (error) {
    return { user: null, error: error.message, status: error.status };
  }

  if (data) {
    // Handle both {user: AuthUser} and AuthUser directly
    const user =
      (data as { user?: AuthUser }).user ??
      (data as AuthUser);

    if (user?.id) {
      // /adm/signin succeeding implies admin
      const adminUser: AuthUser = { ...user, isadmin: true };
      saveUser(adminUser);
      return { user: adminUser };
    }
  }

  return { user: null, error: 'Invalid response from server' };
}

/**
 * Sign out the current user.
 * GET /adm/signout
 */
export async function signOut(): Promise<{ error?: string }> {
  const { error } = await authClient('/signout', {
    method: 'GET',
  });

  // Clear session storage regardless of API response
  clearStoredUser();

  if (error) {
    return { error: error.message };
  }

  return {};
}

/**
 * Change password for the current user.
 * POST /adm/change_password { password }
 */
export async function changePassword(
  newPassword: string
): Promise<{ error?: string }> {
  try {
    await apiClient('/change_password', {
      method: 'POST',
      body: JSON.stringify({ password: newPassword }),
    });
    return {};
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : 'Failed to change password',
    };
  }
}

/**
 * Change email for the current user.
 * POST /adm/change_email { email }
 */
export async function changeEmail(
  newEmail: string
): Promise<{ error?: string }> {
  try {
    await apiClient('/change_email', {
      method: 'POST',
      body: JSON.stringify({ email: newEmail }),
    });
    return {};
  } catch (err) {
    return {
      error: err instanceof Error ? err.message : 'Failed to change email',
    };
  }
}

/**
 * Send a password reset email.
 * POST /adm/forgot_password { email }
 * Backend requires logged-in session; will email a new randomly-generated password.
 */
export async function forgotPassword(
  email: string
): Promise<{ error?: string }> {
  const { error } = await authClient<{ message?: string }>('/forgot_password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

  if (error) return { error: error.message };
  return {};
}

/**
 * Check if user is currently authenticated.
 * Checks sessionStorage — actual session validity is determined by API calls.
 */
export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}

/**
 * Get user display name.
 */
export function getUserDisplayName(user: AuthUser | null): string {
  if (!user) return '';
  if (user.full_name) return user.full_name;
  return user.email.split('@')[0];
}
